// Owner display/edit cell shared by the resume and JD tables. Edit mode is
// entered from the row's "…" menu ("Assign owner"); picking a name PATCHes
// { candidateOwner } to patchPath and refreshes the list query.

import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/hooks/useApi";
import { cn } from "@/lib/utils";
import { Combobox } from "@/components/ui/combobox";

export function OwnerCell({
  value,
  patchPath,
  invalidateKey,
  options,
  onCreate,
  editing,
  onClose,
}: {
  value: string | null;
  patchPath: string;
  /** List query to refresh after a save ("jobs", "jd-jobs"). */
  invalidateKey: string;
  options: string[];
  onCreate: (name: string) => Promise<string>;
  editing: boolean;
  onClose: () => void;
}) {
  const api = useApi();
  const qc = useQueryClient();
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!editing) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [editing, onClose]);

  const save = (candidateOwner: string | null) => {
    onClose();
    void api
      .patch(patchPath, { candidateOwner })
      .then(() => qc.invalidateQueries({ queryKey: [invalidateKey] }))
      .catch(() => undefined);
  };

  if (!editing) {
    return (
      <span className={cn("block max-w-[180px] truncate", value ? "text-text" : "text-text-3")}>
        {value ?? "—"}
      </span>
    );
  }
  return (
    <div ref={wrapRef} className="w-52">
      <Combobox
        autoFocus
        value={value}
        options={options}
        placeholder="Search or add an owner"
        onChange={save}
        onCreate={onCreate}
      />
    </div>
  );
}
